var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * THE COMPOSER'S ONE MENU — attach a file, or choose which model answers.
 *
 * These were three separate controls competing for the bottom-left of a 350px rail: two model
 * pickers whose names truncated to "Claude Sonnet…", plus a paperclip. Three controls to fit meant
 * none of them fit. They are one button now, because they are one kind of thing — everything that
 * shapes the message you are about to send — and a menu can give each row the full width its label
 * needs.
 *
 * THE MODEL ROWS ARE THE SAME <vitesse-model-select> the AI dashboard uses, in its menu-row
 * presentation. Not a lookalike: a second picker built for menus would be a second thing to keep in
 * step, and the two would disagree about capabilities, bans and prices the first time either moved.
 * Each row opens the same two-pane panel, with the same live per-million prices.
 *
 * The choices here are PER-CONVERSATION and are not saved. Durable defaults live in the AI
 * dashboard, which is the only surface that claims to remember anything — so the placeholder shows
 * what would be inherited, and picking here overrides it for this thread alone.
 */
import { LitElement, html, nothing, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { t } from '../i18n';
import { DEFAULT_SPECIALIST, effectiveSpecialist, specialistLabel } from './specialists';
import { LocaleController } from '../i18n/LocaleController';
import { iconButton } from '../elements/shared';
import '../elements/vitesse-icon';
import '../elements/vitesse-model-select';
import '../elements/inputs/vitesse-select';
import '../elements/inputs/vitesse-select';
/** The platform's Effort vocabulary, ordered cheapest-first so the row reads as a dial.
 *  Matches `Effort` in the worker's config and the set OpenRouter accepts, so no translation
 *  table exists here to drift from either. */
const EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'];
let VitesseAiTools = class VitesseAiTools extends LitElement {
    constructor() {
        super(...arguments);
        this.textModels = [];
        this.voiceModels = [];
        /**
         * The IMAGE roster — a third modality, not a third opinion about the same one.
         *
         * Empty on a surface that cannot generate a picture, and the row is omitted entirely rather than
         * rendered blank: a control offering a choice nobody can make reads as a fault.
         */
        this.imageModels = [];
        /** '' = inherit the org default, whose name is shown as the placeholder. */
        this.textValue = '';
        this.voiceValue = '';
        this.imageValue = '';
        /** The model VALUES inherited when nothing is picked — passed as values, not labels, so each row
         *  can resolve the logo and the proper name for what it is actually going to use. */
        this.textDefault = '';
        this.voiceDefault = '';
        this.imageDefault = '';
        /** How hard the model should think. Only meaningful on a model that reasons — see `canReason`. */
        this.effort = 'medium';
        /**
         * WHO ANSWERS — the org's specialists, offered by name.
         *
         * Above the model rows on purpose: choosing a specialist is a bigger decision
         * than choosing a model, and it usually decides the model too. A specialist
         * carries its own prompt, its own tools and its own role, so this is "who am I
         * talking to", while the rows beneath are "what should they think with".
         */
        this.specialists = [];
        /** '' = the ordinary assistant. */
        this.specialist = '';
        this.open = false;
        this.positioned = false;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- re-renders on locale change
        this.locale = new LocaleController(this);
        this.toggle = () => {
            this.open = !this.open;
        };
        this.attach = () => {
            this.open = false;
            this.onAttach?.();
        };
        /**
         * Close on an outside click — ONE LAYER AT A TIME.
         *
         * Two things can be open at once: this menu, and a model row's two-pane panel on top of it. Both
         * listen for an outside click, so a single click away used to collapse the entire stack — you lost
         * the menu as well as the panel, and re-opening it to try another model meant two more clicks.
         *
         * So when a panel is open, this yields: the panel dismisses itself and the menu stays. The NEXT
         * outside click closes the menu. That is what a stack of popovers should do, and it is what the
         * platform does everywhere else.
         *
         * CAPTURE phase is what makes the check trustworthy. Both listeners sit on `document`, so in the
         * bubble phase the panel may already have closed itself before this runs — it would look like
         * nothing was open and close the menu too, which is the bug. Capture observes the state as it was
         * when the click happened.
         *
         * A click inside the panel is NOT outside, even though the top layer puts it visually elsewhere:
         * it is still a DOM descendant of the row, so `composedPath()` — which sees through shadow roots —
         * contains this element.
         */
        this.onDocClick = (e) => {
            if (!this.open)
                return;
            const path = e.composedPath();
            if (path.includes(this))
                return;
            if (this.menuEl && path.includes(this.menuEl))
                return;
            if (this.anyPanelOpen())
                return; // let the inner layer take this click
            this.open = false;
        };
        /** Escape unwinds the same one layer at a time, for the same reason. */
        this.onKey = (e) => {
            if (e.key !== 'Escape' || !this.open)
                return;
            if (this.anyPanelOpen())
                return; // the panel takes this one
            this.open = false;
        };
        this.reposition = () => {
            if (this.open)
                this.position();
        };
    }
    /** Is the default one actually in this roster? It is absent on a surface whose
     *  specialists are all declared elsewhere, and on any deployment where it has
     *  not been provisioned yet. */
    hasDefault() {
        return this.specialists.some((e) => e.id === DEFAULT_SPECIALIST);
    }
    specialistOptions() {
        const rows = this.specialists.map((e) => ({ value: e.id, label: specialistLabel(e) }));
        // Nothing to stand in for the ordinary assistant, so name it explicitly.
        return this.hasDefault() ? rows : [{ value: '', label: t('ai.specialistDefault') }, ...rows];
    }
    /** The SAME resolution the conversation scopes its tools with, so the name shown here and the
     *  tools actually offered can never describe different specialists. */
    currentSpecialist() {
        return effectiveSpecialist(this.specialist, this.specialists);
    }
    static { this.styles = [
        iconButton,
        css `
      :host {
        position: relative;
        display: inline-flex;
      }
      /*
       * TOP LAYER, like every other panel in this app.
       *
       * The rail clips its own overflow, so a menu positioned inside it would be cut off at the
       * wall. The Popover API takes it out of the flow entirely — the same fix <vitesse-select>,
       * the model picker and the modal dialog all use.
       */
      .menu {
        position: fixed;
        min-width: 262px;
        max-width: min(320px, 92vw);
        padding: 5px;
        margin: 0;
        inset: auto;
        border: 1px solid var(--border-color);
        border-radius: 12px;
        background: var(--card-bg);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.42);
        animation: in 0.14s ease-out;
      }
      .menu:not(:popover-open) {
        display: none;
      }
      @keyframes in {
        from {
          opacity: 0;
          transform: translateY(4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      /* The attach row is a plain menu row; the model rows below are the picker in its row form,
         which styles itself. */
      .attach {
        display: flex;
        align-items: center;
        gap: 9px;
        width: 100%;
        box-sizing: border-box;
        padding: 8px 9px;
        border: 0;
        border-radius: 9px;
        background: transparent;
        color: var(--text-primary);
        font: inherit;
        font-size: 13.5px;
        text-align: left;
        cursor: pointer;
        transition: background 0.13s ease;
      }
      /* A row, not an icon button: the label is the affordance and the glyph rides along. It picks
         up the accent on hover rather than a grey wash — the wash reads as a disabled surface next
         to the two live model rows beneath it. */
      .attach:hover {
        color: var(--blue);
      }
      .attach:hover vitesse-icon {
        color: var(--blue);
      }
      /*
       * ONE WIDTH FOR BOTH BADGES.
       *
       * "Text Mode" and "Voice Mode" are different lengths, so left to themselves the two rows put
       * their chevrons in different places and the menu looks ragged. A shared min-width with
       * centred content lines the columns up; the badges keep their own colours.
       */
      vitesse-model-select::part(badge) {
        min-width: 92px;
      }
      .sep {
        height: 1px;
        margin: 5px 4px;
        background: var(--border-color);
      }
      @media (prefers-reduced-motion: reduce) {
        .menu {
          animation: none;
        }
      }
      /* The thinking row. Same rhythm as the menu's other rows so the menu reads as one list. */
      /* The same row shape as Effort beneath it: a name on the left, the control on
         the right. Above the model rows because choosing WHO answers is the bigger
         decision, and it usually decides the model too. */
      .specialist {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 7px 10px;
        font-size: 13px;
      }
      /* FIVE ROWS, then scroll. A menu that grows with the roster eventually runs
         past the viewport and pushes its own trigger off-screen; five is enough to
         see the shape of the list and short enough to stay a menu. */
      .specialist-select {
        --vitesse-select-menu-max-height: 190px;
      }
      .specialist-label {
        font-size: 13px;
        color: var(--text-primary);
        white-space: nowrap;
        flex: 1;
      }
      .effort {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 7px 10px;
        font-size: 13px;
      }
      /* The row's name, in the same voice as Attach and the model rows above it. */
      .effort-end {
        font-size: 13px;
        color: var(--text-primary);
        white-space: nowrap;
        flex: 1;
      }
      /* Where you are, in words — the dots carry the position, this says which stop it is. */
      .effort-now {
        font-size: 11px;
        color: var(--text-tertiary);
        white-space: nowrap;
        min-width: 44px;
        text-align: right;
      }

      /*
       * FIVE DOTS AND THE NAME OF WHERE YOU ARE.
       *
       * It was a segmented bar between the words "Fast" and "Smart" — three pieces of chrome to say
       * one number, and the ends had to be read to know what the middle meant. The dots carry the
       * position and the label says which stop it is, so the control explains itself with less on
       * screen: the row now reads like every other row in this menu (a name, then its value).
       */
      .effort-dots {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      .effort-step {
        width: 7px;
        height: 7px;
        padding: 0;
        border: 0;
        border-radius: 50%;
        background: var(--border-color);
        cursor: pointer;
        transition:
          background 0.12s ease,
          transform 0.12s ease;
      }
      /*
       * THE GRADIENT RUNS ACROSS THE DOTS, not within one.
       *
       * The lit dots ramp from a washed blue at the first stop to the full accent at the last, so
       * the control shows its own scale: how far along you are is visible in the COLOUR as well as
       * in how many are filled. A single flat blue made five dots say only "some", and needed the
       * label beside them to say which. The spread is wide on purpose — a subtle ramp reads as an
       * inconsistency in one colour rather than as a scale.
       *
       * Written as nth-child rather than an inline style per dot, because the ramp is a property of
       * the control and not of the value — and because inline styles are not how this codebase
       * paints anything.
       */
      .effort-step.on:nth-child(1) {
        background: color-mix(in srgb, var(--blue) 22%, var(--border-color));
      }
      .effort-step.on:nth-child(2) {
        background: color-mix(in srgb, var(--blue) 42%, var(--border-color));
      }
      .effort-step.on:nth-child(3) {
        background: color-mix(in srgb, var(--blue) 62%, var(--border-color));
      }
      .effort-step.on:nth-child(4) {
        background: color-mix(in srgb, var(--blue) 80%, var(--border-color));
      }
      .effort-step.on:nth-child(5) {
        background: var(--blue);
      }
      /* Hover is the full accent whatever the stop — it is a preview of picking it, not of its
         place on the ramp. */
      .effort-step:hover {
        background: var(--blue);
        transform: scale(1.25);
      }
    `,
    ]; }
    /**
     * Can the model that will actually answer reason?
     *
     * Resolved against the EFFECTIVE model — the explicit pick when there is one, otherwise the org
     * default this row is inheriting. Reading only `textValue` would hide the control on every fresh
     * conversation, which is exactly when nobody has picked anything yet.
     *
     * Unknown models (a BYO endpoint, a catalog that has not loaded) answer false: showing the row
     * would offer a knob whose effect nobody can predict, and a missing control is a smaller lie than
     * one that does nothing.
     */
    get canReason() {
        return this.textModels.some((m) => m.value === this.effectiveText && m.reasoning === true);
    }
    /**
     * THE MODEL THAT WILL ACTUALLY ANSWER — shown, always, rather than a placeholder.
     *
     * The row was bound to `textValue`, the pick made FOR THIS CONVERSATION, which is empty until
     * somebody picks one. So an existing thread showed "Chat model" and a lightbulb row that had
     * vanished, while a real model was answering every turn — the control described a state that was
     * never true. There is no such thing as "no model": the turn resolves the same chain regardless
     * (the conversation's pick, then the org default), so the row shows the end of that chain.
     */
    get effectiveText() {
        return this.textValue || this.textDefault;
    }
    get effectiveVoice() {
        return this.voiceValue || this.voiceDefault;
    }
    get effectiveImage() {
        return this.imageValue || this.imageDefault;
    }
    render() {
        return html `
      <!--
        LEVERS, NOT A PLUS. What opens here is model, thinking effort, specialist and attachments —
        a set of things to SET, and a plus promises something to add. The glyph now says what the
        menu is, which is the difference between a control you learn once and one you open to find out.
      -->
      <button
        class="icon-btn md plus"
        title=${t('ai.toolsMenu')}
        aria-label=${t('ai.toolsMenu')}
        aria-expanded=${this.open}
        @click=${this.toggle}
      >
        <vitesse-icon name="sliders-horizontal" size="18"></vitesse-icon>
      </button>

      <div class="menu" popover="manual">
        <!--
          ONLY WHEN THERE IS A PICKER BEHIND IT. onAttach has always been documented as
          "absent = no attach affordance, which is honest on a host that has not wired a picker
          rather than drawing a button that does nothing" - and the row was drawn unconditionally,
          so in the chat (which wires no picker) the paperclip opened, closed the menu, and did
          nothing at all. The intent was written down and never implemented.
        -->
        ${this.onAttach
            ? html `<button class="attach" @click=${this.attach}>
                <vitesse-icon name="paperclip" size="16"></vitesse-icon>${t('ai.attach')}
              </button>`
            : nothing}

        <!--
          FAST ↔ SMART, directly under Attach — both are things you set about the message, above the
          rows that choose who answers it.

          NO LABEL AND NO ICON. The two ends say what the control does, and a word in front of them
          ("Effort", and before that "Thinking") only adds a term to learn: the axis is the sentence.
          It was also drawn TWICE, once here and once under the models, so a model that could reason
          showed the control in both places.

          A SEGMENTED BAR rather than a range input. A native slider draws a hairline and a handle,
          which reads as continuous and invites dragging for a value that has five stops; the bar
          shows the stops, fills to the current one, and is clicked rather than dragged. Rendered
          only when the effective model can reason — a control beside a model that cannot think is a
          promise the turn will not keep.
        -->
        ${this.canReason
            ? html `<div class="effort">
                <span class="effort-end">${t('ai.thinkingEffort')}</span>
                <span class="effort-dots" role="group" aria-label=${t('ai.thinkingEffort')}>
                  ${EFFORTS.map((e, i) => html `<button
                        class="effort-step ${EFFORTS.indexOf(this.effort) >= i ? 'on' : ''}"
                        title=${t(`ai.effort_${e}`)}
                        aria-label=${t(`ai.effort_${e}`)}
                        aria-pressed=${this.effort === e ? 'true' : 'false'}
                        @click=${() => this.onEffort?.(e)}
                      ></button>`)}
                </span>
                <span class="effort-now">${t(`ai.effort_${this.effort}`)}</span>
              </div>`
            : nothing}

        ${this.specialists.length
            ? html `<div class="sep"></div>
                <div class="specialist">
                  <span class="specialist-label">${t('ai.specialist')}</span>
                  <!-- The "no specialist" row appears ONLY when the roster has no
                       default one to stand in for it. With it present, offering
                       both was the same thing listed twice - once as an absence,
                       once as itself. Without it, dropping the row left the select
                       holding a value none of its options matched, which renders
                       as a picker with nothing in it. -->
                  <vitesse-select
                    size="sm"
                    class="specialist-select"
                    .options=${this.specialistOptions()}
                    .value=${this.currentSpecialist()}
                    @change=${(e) => this.onSpecialist?.(e.detail.value)}
                  ></vitesse-select>
                </div>`
            : nothing}

        <div class="sep"></div>

        <!-- Text on top, voice beneath: the order they are reached for. -->
        <!-- NOT clearable: clearing it would mean "answer with nothing", which is not a state the
             turn can be in. The chain always resolves to a model, so the row always names one. -->
        <vitesse-model-select
          row
          badge=${t('ai.textMode')}
          badgeTone="blue"
          badgeIcon="keyboard"
          .models=${this.textModels}
          .value=${this.effectiveText}
          placeholderModel=${this.textDefault}
          placeholder=${t('ai.chatModel')}
          @change=${(e) => this.onTextModel?.(e.detail.value)}
        ></vitesse-model-select>
        <vitesse-model-select
          row
          badge=${t('ai.voiceMode')}
          badgeTone="purple"
          badgeIcon="audio-lines"
          .models=${this.voiceModels}
          .value=${this.effectiveVoice}
          placeholderModel=${this.voiceDefault}
          placeholder=${t('ai.voiceModel')}
          @change=${(e) => this.onVoiceModel?.(e.detail.value)}
        ></vitesse-model-select>
        ${
        /*
         * AND THE PICTURE, third, because it is the one that is not always there.
         *
         * Text and voice are two ways of answering the SAME question, so they sit together at the
         * top; an image is a different thing to ask for, and it comes last for the same reason
         * the specialist row comes first — the list reads in the order somebody reaches for it.
         *
         * OMITTED, not disabled, when this deployment has no image roster. An empty picker is a
         * control that offers a choice nobody can make, which reads as a fault rather than as an
         * absence.
         */
        this.imageModels.length
            ? html `<vitesse-model-select
                row
                badge=${t('ai.imageMode')}
                badgeTone="green"
                badgeIcon="image"
                .models=${this.imageModels}
                .value=${this.effectiveImage}
                placeholderModel=${this.imageDefault}
                placeholder=${t('ai.imageModel')}
                @change=${(e) => this.onImageModel?.(e.detail.value)}
              ></vitesse-model-select>`
            : nothing}
      </div>
    `;
    }
    connectedCallback() {
        super.connectedCallback();
        // CAPTURE phase, deliberately — see onDocClick.
        document.addEventListener('click', this.onDocClick, true);
        document.addEventListener('keydown', this.onKey);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this.onDocClick, true);
        document.removeEventListener('keydown', this.onKey);
        window.removeEventListener('resize', this.reposition);
    }
    /** Is one of the model rows showing its panel? */
    anyPanelOpen() {
        return [...this.renderRoot.querySelectorAll('vitesse-model-select')].some((el) => el.isOpen);
    }
    updated() {
        const m = this.menuEl;
        if (!m)
            return;
        if (this.open && !this.positioned) {
            try {
                m.showPopover();
            }
            catch {
                /* already shown */
            }
            this.position();
            this.positioned = true;
            window.addEventListener('resize', this.reposition);
        }
        else if (!this.open && this.positioned) {
            try {
                m.hidePopover();
            }
            catch {
                /* already hidden */
            }
            window.removeEventListener('resize', this.reposition);
            this.positioned = false;
        }
    }
    /** Above the button by default — this sits at the BOTTOM of a rail, so there is no room below. */
    position() {
        const b = this.plusEl;
        const m = this.menuEl;
        if (!b || !m)
            return;
        const r = b.getBoundingClientRect();
        const w = m.offsetWidth || 262;
        m.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - w - 8))}px`;
        const h = m.offsetHeight || 160;
        // Flip below only when there genuinely is not room above.
        if (r.top - h - 8 < 8 && window.innerHeight - r.bottom > h) {
            m.style.top = `${r.bottom + 6}px`;
            m.style.bottom = 'auto';
        }
        else {
            m.style.bottom = `${window.innerHeight - r.top + 6}px`;
            m.style.top = 'auto';
        }
    }
};
__decorate([
    property({ attribute: false })
], VitesseAiTools.prototype, "textModels", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiTools.prototype, "voiceModels", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiTools.prototype, "imageModels", void 0);
__decorate([
    property()
], VitesseAiTools.prototype, "textValue", void 0);
__decorate([
    property()
], VitesseAiTools.prototype, "voiceValue", void 0);
__decorate([
    property()
], VitesseAiTools.prototype, "imageValue", void 0);
__decorate([
    property()
], VitesseAiTools.prototype, "textDefault", void 0);
__decorate([
    property()
], VitesseAiTools.prototype, "voiceDefault", void 0);
__decorate([
    property()
], VitesseAiTools.prototype, "imageDefault", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiTools.prototype, "onAttach", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiTools.prototype, "onTextModel", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiTools.prototype, "onVoiceModel", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiTools.prototype, "onImageModel", void 0);
__decorate([
    property()
], VitesseAiTools.prototype, "effort", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiTools.prototype, "onEffort", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiTools.prototype, "specialists", void 0);
__decorate([
    property()
], VitesseAiTools.prototype, "specialist", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiTools.prototype, "onSpecialist", void 0);
__decorate([
    state()
], VitesseAiTools.prototype, "open", void 0);
__decorate([
    query('.menu')
], VitesseAiTools.prototype, "menuEl", void 0);
__decorate([
    query('.plus')
], VitesseAiTools.prototype, "plusEl", void 0);
VitesseAiTools = __decorate([
    customElement('vitesse-ai-tools')
], VitesseAiTools);
export { VitesseAiTools };
//# sourceMappingURL=tools-menu.js.map